<template>
  <div
    class="line-item"
    :class="[
      $blockClass(blockClass, 'line-item'),
      {
        [$class('loading')]: !item,
        [$class('updating')]: state === 'updating'
      },
    ]"
  >
    <a :href="item.url">
      <responsive-image
        :class="$blockClass(blockClass, 'line-item-image')"
        block-class="line-item"
        :image="item.featured_image"
        :lazy-load="false"
        min-max="113-345"
        ratio="3:4"
        sizes="113px"
        :style="{ animationDelay: animationDelay }"
      />
    </a>

    <div
      class="line-item__body"
      :class="$blockClass(blockClass, 'line-item-body')"
      :style="{ animationDelay: animationDelay }"
    >
      <div class="line-item__content">
        <div class="line-item__content-area">
          <div class="line-item__information">
            <a
              :href="vendorCollectionUrl(item)"
              tabindex="-1"
              class="text-heading-3xs text-heading-xs-desktop"
            >
              <p
                class="line-item__vendor text-heading-3xs text-heading-xs-desktop"
                :class="$blockClass(blockClass, 'line-item-vendor')"
                v-text="item.vendor"
              />
            </a>

            <a
              :href="item.url"
              tabindex="-1"
              class="text-body-xs text-body-s-desktop"
            >
              <p
                class="line-item__title text-body-xs text-body-s-desktop"
                :class="$blockClass(blockClass, 'line-item-title')"
                v-text="itemTitle"
              />
            </a>

            <p
              class="line-item__variant text-body-xs text-body-s-desktop"
              :class="$blockClass(blockClass, 'line-item-variant')"
              v-text="getVariantTitle(item)"
            />

            <div :class="{ 'line-item__price-wrapper': showQuantity }">
              <p
                v-if="showQuantity"
                class="text-body-xs text-body-s-desktop"
                v-text="$string('cart.line_item.1x')"
              />

              <product-prices
                block-class="line-item"
                class="text-body-s"
                :class="$blockClass(blockClass, 'line-item-prices')"
                :compare-at-price="item?.compare_at_price || false"
                :price="item?.price"
              />
            </div>
          </div>
        </div>

        <div
          v-if="showFooter"
          class="line-item__footer"
          :class="$blockClass(blockClass, 'line-item-footer')"
        >
          <quantity-selector
            :class="$blockClass(blockClass, 'line-item-quantity')"
            block-class="line-item"
            :item="item"
            :quantity="quantity"
            :state="state"
            @update-quantity="handleQuantityUpdate"
            @remove-item="handleRemove"
          />

          <button
            class="line-item__remove icon-button"
            :class="[
              $blockClass(blockClass, 'line-item-remove'),
              { [$class('disabled')]: state === 'updating' },
            ]"
            type="button"
            @click="handleRemove"
          >
            <p
              class="text-body-xs"
              v-text="$string('cart.line_item.remove')"
            />
            <icon-close />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Line item (line-item)
 * -----------------------------------------------------------------------------
 * Displays line items in cart, cart drawer, and order.
 *
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {Number} [index] - Line item's forloop index.
 * @param {Boolean|Object} item - Line item in cart response or order.
 * @param {Boolean} showFooter - Decides to hide/show footer for line item.
 * @param {Boolean} showQuantity - Show/hide quantity for line item.
 *
 */
import { mapActions, mapState } from 'vuex'

import ProductPrices from '~global/product-prices/product-prices'
import QuantitySelector from '~async/quantity-selector/quantity-selector'
import ResponsiveImage from '~global/images/responsive-image'

import IconClose from '~icons/misc/close.svg'

export default {
  name: 'LineItem',

  components: {
    IconClose,
    ProductPrices,
    QuantitySelector,
    ResponsiveImage,
  },

  props: {
    blockClass: {
      type: String,
      default: '',
    },
    index: {
      type: Number,
      default: 0,
    },
    item: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    showFooter: {
      type: Boolean,
      default: true,
    },
    showQuantity: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      quantity: 1,
      state: 'loading',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      cartStatus: (state) => state.cart.status,
    }),

    /**
     * Compute animation delay.
     * @returns {String}
     */
    animationDelay() {
      return this.$timing('quick', { index: this.index, unit: 's' })
    },

    /**
     * Compute item title.
     * @returns {String}
     */
    itemTitle() {

      /* _hide prefix to hide the property in checkout page */
      // eslint-disable-next-line no-underscore-dangle
      return this.item.properties._hide_product_title || this.item.product_title
    },
  },

  watch: {

    /**
     * Watch cart status items to see if item is being updated.
     */
    'cartStatus.items'() {
      if (this.cartStatus.items.includes(this.item.key)) {
        this.state = 'updating'
        return
      }

      this.state = 'ready'
    },

    /**
     * Watch item quantity to update quantity if updated elsewhere.
     */
    'item.quantity'() {
      this.quantity = this.item.quantity
    },
  },

  mounted() {
    this.state = 'ready'
    this.quantity = this.item.quantity
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      updateItem: 'cart/updateItem',
      removeItem: 'cart/updateItem',
    }),

    /**
     * Handle quantity update event.
     * @param {String} quantity - New quantity.
     */
    async handleQuantityUpdate(quantity) {
      this.quantity = quantity

      try {
        this.state = 'updating'

        await this.updateItem([
          {
            id: this.item.key,
            quantity: this.quantity,
          },
        ])

        this.state = 'ready'

      } catch (error) {
        this.quantity = this.item.quantity
        this.state = 'error'
      }
    },

    /**
     * Handle remove click.
     */
    handleRemove() {
      if (this.state === 'updating') {
        return
      }

      this.removeItem(this.item.key)
    },

    /**
     * Compute vendor collection URL.
     * @param {Object} item
     * @returns {String}
     */
    vendorCollectionUrl(item) {
      return item.vendor_link
        ? item.vendor_link
        : `${cnvs.routes.normalisedRoot}collections/vendors?q=${encodeURI(item.vendor)}`
    },

    /**
     * Returns formatted variant title.
     * @param {Object} item
     * @return {String}
     */
    getVariantTitle(item) {
      return item.variant_title?.replace('/', '|')
    },
  },
}
</script>

<style lang="scss">
@import './line-item';
</style>
